<template>
  <v-expansion-panel>
    <v-expansion-panel-title>
      <span>
        <div>
          {{ dataSourceLocal.name }}
          <RfButton
            icon="md:edit"
            color="accent"
            iconSize="16"
            class="mt-n1 ml-2"
            size="x-small"
            variant="text"
            :disabled="userStore.isDisabledWithMessage || dataSourceLocal.is_locked"
            @click.stop="
              $refs.renameModal.open({
                name: dataSourceLocal.name,
                description: dataSourceLocal.description,
              })
            "
          >
            <RfEditModal
              ref="renameModal"
              :title="`Edit datasource ${dataSourceLocal.name}`"
              @confirm="v => onEdit(v)"
            />
          </RfButton>
        </div>
        <div v-if="dataSourceLocal.description" class="text-caption text--secondary text-none">
          font-size: 0.75rem; font-weight: 400; line-height: 1.667; letter-spacing: 0.0333333333em;
          {{ dataSourceLocal.description }}
        </div>
      </span>
    </v-expansion-panel-title>
    <v-expansion-panel-text eager>
      <div class="flex flex-col">
        <div class="my-5 mr-0 ml-auto flex items-center">
          <RfButton
            color="accent"
            icon="md:upload"
            class="mr-2"
            :disabled="userStore.isDisabledWithMessage"
            buttonText="Upload CSV"
            @click="$refs.csvInput.click()"
          >
            <input
              ref="csvInput"
              type="file"
              class="hidden"
              :disabled="!!userStore.isDisabledWithMessage"
              accept=".csv"
              @change="v => emit('uploadCSV', v, dataSourceLocal.id)"
            />
          </RfButton>
          <RfButton
            color="error"
            icon="md:clear"
            :disabled="userStore.isDisabledWithMessage"
            buttonText="Delete"
            @click="emit('remove', dataSourceLocal)"
          >
          </RfButton>
        </div>
        <v-table class="rf-settings-table-data">
          <thead>
            <th>Name</th>
            <th>Settings</th>
          </thead>
          <tbody v-if="Object.keys(dataSourceLocal.properties).length">
            <tr v-for="(settings, field) in dataSourceLocal.properties" :key="field">
              <td>{{ field }}</td>
              <td>
                <v-checkbox
                  v-for="(value, key) in settings"
                  :key="key"
                  :label="StringUtils.capitalize(getPrettyPropertyName(key))"
                  class="mt-0 pt-0"
                  :disabled="
                    userStore.isDisabledWithMessage ||
                    isAllowedToUseAsDisplayName(field, key, dataSourceLocal.properties)
                  "
                  hideDetails
                  :modelValue="value"
                  @update:modelValue="
                    v => {
                      dataSourceLocal.properties[field][key] = v;
                      editDataSource();
                    }
                  "
                ></v-checkbox>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td colspan="7">
                <div class="settings-no-data-message">Nothing to show</div>
              </td>
            </tr>
          </tbody>
        </v-table>
      </div>
    </v-expansion-panel-text>
  </v-expansion-panel>
</template>

<script setup>
import RfButton from "@/components/buttons/RfButton.vue";
import RfEditModal from "@/components/modals/RfEditModal.vue";
import StringUtils from "@/utils/StringUtils";
import { useUserStore } from "@/pinia/userStore";
import { watch, ref } from "vue";
import { cloneDeep } from "lodash-es";
import { debounce } from "@/utils/debounce";

const emit = defineEmits(["edit", "remove", "uploadCSV"]);
const props = defineProps({ dataSource: { type: Object, required: true } });

const userStore = useUserStore();
const dataSourceLocal = ref(cloneDeep(props.dataSource));
const editDataSource = debounce(() => emit("edit", dataSourceLocal.value), 2000);

const onEdit = v => emit("edit", { ...dataSourceLocal.value, ...v });
const getPrettyPropertyName = key => (key === "use_as_display_name" ? "Use as display name" : key);
const isAllowedToUseAsDisplayName = (field, key, properties) => {
  if (key !== "use_as_display_name") return false;
  return !!Object.entries(properties || {}).find(([f, v]) => f !== field && v.use_as_display_name);
};

watch(
  () => props.dataSource,
  v => (dataSourceLocal.value = cloneDeep(v)),
  { deep: true },
);
</script>

<style scoped>
.rf-settings-table-data th {
  width: 50%;
}
</style>
